// Angular Imports
// =========================================================
import { Component, ViewChild } from '@angular/core'
// AG Grid Imports
// =========================================================
import { type IToolPanelParams } from 'ag-grid-community'
import { type IToolPanelAngularComp } from 'ag-grid-angular'
// Prime NG Imports
// =========================================================
import { Tree } from 'primeng/tree'
import { type TreeNode } from 'primeng/api'
// Custom Imports
// =========================================================
import { FormatKey, IsKeyInObj } from 'src/app/utils/global_functions'
import { type AccordionHeaderPanelStateInterface } from 'src/app/core/interfaces/shared-component-interfaces'
import { SizeDistroDefaultParametersAPIs } from 'src/app/core/apis/size-distro-default-parameters_api-calls'
import { SaveGridState } from 'src/app/05_ag-grid-configs/02_global-settings/grid-functions-general'

// Advanced Filtering side-bar tool panel for the Review Orders "User Input"
// (Job List) grid -- Product Filters only, following the same product-tree
// pattern as SizeDistroManagementSummaryToolPanelComponent, trimmed down
// since Review Orders only needs the one filter type.
@Component({
  standalone: false,
  selector: 'app-review-orders-user-input-tool-panel',
  templateUrl: './review-orders-user-input-tool-panel.component.html',
  styleUrls: ['./review-orders-user-input-tool-panel.component.scss']
})
export class ReviewOrdersUserInputToolPanelComponent {
  parentGrid
  ObjectKeys = Object.keys
  private params: IToolPanelParams

  // Params for Product Hierarchy Section
  treeData: TreeNode[] = []
  treeDataInitializing: boolean = false

  loadingProdHier: boolean = true
  loadingNodeChildren: boolean = false
  selectedHierarchyNode: TreeNode = null
  defaultSelected: TreeNode[] = []
  allSelectedProductNodes: any = []
  openPanels: string[] = ['product_filters']
  expandedNodes: string[] = []

  @ViewChild('prodHierTreeData') tree!: Tree

  constructor (private readonly sizeDistroDefaultParametersAPIs: SizeDistroDefaultParametersAPIs) {}

  agInit (params: IToolPanelParams): void {
    this.params = params
    this.parentGrid = this.params?.context?.componentParent
    console.log('---> Review Orders Advanced Filtering Tool Panel: ', this.params)

    // Listen for onModelUpdated to set the filters
    this.params.api.addEventListener('modelUpdated', this.updateExternalFilters.bind(this))

    // The panel is closed by default, so it may only get instantiated well
    // after the product tree has already finished loading -- sync against
    // whatever's already there immediately, rather than waiting on a future
    // modelUpdated/fetch-resolved event that may never come again.
    this.updateExternalFilters(null)
  }

  // Track the accordion panel state to adjust the heights of the accordion bodies
  onPanelStateChanged (event: AccordionHeaderPanelStateInterface) {
    const { state, section } = event
    const panelIndex = this.openPanels.indexOf(section)
    if (state === 'opened' && panelIndex === -1) {
      this.openPanels.push(section)
    } else if (state === 'closed' && panelIndex > -1) {
      this.openPanels.splice(panelIndex, 1)
    }
  }

  // Give each childnode access to the parent node
  wireParents (nodes, parent: TreeNode) {
    if (nodes) {
      return nodes.map(node => {
        node.data['parent'] = parent
        return this.wireParents(node.children, node)
      })
    }
  }

  // Set the default values for the Product Filters section
  async updateExternalFilters (event) {
    if (
      (this.parentGrid.productTreeData && this.parentGrid.productTreeData.length > 0) ||
            this.parentGrid.productFilters.length !== this.allSelectedProductNodes.length
    ) {
      this.allSelectedProductNodes = []
      this.treeData = this.parentGrid.productTreeData
      try {
        await this.wireParents(this.treeData, null)
      } finally {
        const setNodeSelection = node => {
          this.treeDataInitializing = true
          const children = node.children.map(childNode => setNodeSelection(childNode)) || []
          node.children = children
          node.data.is_selected = this.parentGrid.productFilters.includes(node.key)

          if (this.parentGrid.productFilters.includes(node.key)) {
            const parent = node.parent || node?.data?.parent
            this.allSelectedProductNodes.push(node)
            this.setParentsPartialSelected(parent, this.parentGrid.productFilters)
          }
          return node
        }

        if (!this.treeDataInitializing) {
          this.loadingProdHier = false

          const mappedData = await this.treeData.map(node => setNodeSelection(node))
          if (mappedData) {
            this.treeDataInitializing = false
          }
        }
      }
    }
  }

  // Set Partial Selected at all Levels
  setParentsPartialSelected (node: TreeNode, selectedKeys: string[]) {
    if (node && !selectedKeys.includes(node.key)) {
      const parent = node.parent || node?.data?.parent
      const selectedChildren = node.children.filter(child => child.data.is_selected) || []
      const allChildrenSelected = selectedChildren.length === node.children.length

      node.partialSelected = selectedChildren.length === 0 ? null : !allChildrenSelected
      node.data.is_selected = selectedChildren.length !== 0

      if (selectedChildren.length > 0 && allChildrenSelected) {
        this.setParentsPartialSelected(parent, [...selectedKeys, node.key])
      } else {
        this.setParentsPartialSelected(parent, selectedKeys)
      }
    }
  }

  // Update Node Selection and Partial Selection
  async onNodeSelectionChange (event, isSelected) {
    const updateNodeSelection = node => {
      node.data.is_selected = isSelected

      if (node.children.length > 0) {
        node.children = node.children.map(childNode => updateNodeSelection(childNode))
        const selectedChildren = node.children.filter(child => child.data.is_selected) || []
        const allChildrenSelected = selectedChildren.length === node.children.length
        node.partialSelected = selectedChildren.length === 0 ? null : !allChildrenSelected
      }
      return node
    }

    let selectedNodes

    try {
      // Every fully-selected top-level node's own descendant-key list is
      // cached (see selectedNodeKeysCache) -- re-walking every already-selected
      // branch on every single click doesn't scale once most of the tree is
      // checked, so only the node that actually changed gets re-walked.
      selectedNodes = [
        ...new Set(this.allSelectedProductNodes.flatMap(node => this.getCachedNodeAndDescendantKeys(node)))
      ]
      await updateNodeSelection(event.node)
    } finally {
      this.selectedNodeKeysCache.delete(event.node.key)
      this.parentGrid.productFilters = selectedNodes
    }
  }

  // Cache of node.key -> flattened [node.key, ...descendant keys], reused
  // across clicks for any already-selected branch that didn't just change.
  private readonly selectedNodeKeysCache = new Map<string, string[]>()

  private getNodeAndDescendantKeys (node): string[] {
    if (node.children.length > 0) {
      return [node.key, ...node.children.flatMap(childNode => this.getNodeAndDescendantKeys(childNode))]
    } else if (node.key) {
      return [node.key]
    }
    return []
  }

  private getCachedNodeAndDescendantKeys (node): string[] {
    if (!this.selectedNodeKeysCache.has(node.key)) {
      this.selectedNodeKeysCache.set(node.key, this.getNodeAndDescendantKeys(node))
    }
    return this.selectedNodeKeysCache.get(node.key)
  }

  // Apply the Product Filters -- persist the selection to the saved grid
  // view, then re-fetch the Job List filtered to the selected products
  async onApplyAllFilters (event) {
    this.params.api.showLoadingOverlay()
    await SaveGridState(this.parentGrid.gridApi, this.parentGrid.gridOptions, 'user_input')
    await this.parentGrid.applyProductFilters()
  }

  // Reset the Product Filters
  async onResetProductFilters (event) {
    const updateNodeSelection = node => {
      node.data.is_selected = false
      if (node.children.length > 0) {
        const parent = node.parent || node?.data?.parent

        node.children = node.children.map(childNode => updateNodeSelection(childNode))
        this.setParentsPartialSelected(parent, [])
      }

      return node
    }
    try {
      this.parentGrid.productFilters = []
      await this.treeData.map(node => updateNodeSelection(node))
    } finally {
      return (this.allSelectedProductNodes = [])
    }
  }

  async onResetAllFilters (event) {
    this.onResetProductFilters(null)
  }

  toggleNodeExpanded (data) {
    const node: TreeNode = IsKeyInObj(data, 'key') ? data : data.node
    const formKey = FormatKey(node.key)

    if (node.expanded && !this.expandedNodes.includes(formKey)) {
      this.expandedNodes.push(formKey)
      this.fetchNodeChildren(node)
    } else if (!node.expanded && this.expandedNodes.includes(formKey)) {
      this.expandedNodes.splice(this.expandedNodes.indexOf(formKey), 1)
    }
  }

  fetchNodeChildren (node: TreeNode) {
    if (node.children.length && node.children[0].key === 'fakekey') {
      this.loadingNodeChildren = true
      this.sizeDistroDefaultParametersAPIs.GetProductTreeNode({ queryString: '?a=a' }, node.key).then(res => {
        node.children = JSON.parse(res.data).map((childNode: string) => {
          const child = JSON.parse(childNode)
          child.parent = node
          return child
        })
        // The real children just replaced the lazy-load placeholder, so any
        // cached descendant-key list for this node -- or an ancestor whose
        // cached list was computed while this subtree was still a placeholder
        // -- is now stale.
        let nodeToInvalidate: any = node
        while (nodeToInvalidate) {
          this.selectedNodeKeysCache.delete(nodeToInvalidate.key)
          nodeToInvalidate = nodeToInvalidate.parent || nodeToInvalidate?.data?.parent
        }
        this.tree.updateSerializedValue()
        this.loadingNodeChildren = false
      })
    }
  }
}
